/**
 * @feature landing-page
 * @owner das-finsuite-application-dev
 * @description Landing page constants — section titles, card data, and promo content
 */

export const WELCOME_BANNER = {
  promoLinkHref: '/support'
} as const;

export const SECTION_TITLES = {
  quickActions: 'QUICK ACTIONS',
  recentsFeatured: 'FEATURED APPS',
  dueThisWeek: "What's Due This Week",
  testimonials: 'TESTIMONIALS'
} as const;

export interface QuickLinkItem {
  label: string;
  href: string;
  external?: boolean;
  tooltip?: string;
  /** LDAP groups allowed to see this link. Omit = visible to all. Mirrors the route's accessibleTo. */
  accessibleTo?: string[];
  /** LDAP groups that gain visibility only in non-prod stages (dev/alpha). Folded into the allow-list when IS_NON_PROD. */
  nonProdOnlyAccessibleTo?: string[];
  /** Shown only in dev/alpha, to everyone there. Mirrors the route's `nonProdOnly`. */
  nonProdOnly?: boolean;
  /** Hides the link on prod only (alpha/beta keep it). Mirrors the route's environmentRestriction: 'non-prod'. */
  hiddenInProd?: boolean;
}

export interface QuickLinksSection {
  title: string;
  links: QuickLinkItem[];
  external?: boolean;
}

export const QUICK_LINKS_SECTIONS: QuickLinksSection[] = [
  {
    title: 'EXPLORE FINSUITE',
    links: [
      { label: 'All Apps', href: '/all-apps' },
      { label: 'Product Launches', href: '/announcements' }
    ]
  },
  {
    title: 'ORG TOOLS',
    // Keep these links sorted alphabetically by `label`
    links: [
      {
        label: 'Airspace',
        href: '/capacity-planner',
        tooltip: 'Weekly Capacity Allocation by Person and Project'
      },
      { label: 'Baseball Cards', href: '/baseball-cards' },
      {
        label: 'Canary',
        href: '/canary',
        tooltip: 'Connection intelligence for leaders (Connections scorecards)',
        // Mirrors eLDAPGroup in src/constants/AppConstants (inlined to keep this a zero-import
        // constants module; referencing the enum here caused undefined-at-eval under jest mocks).
        accessibleTo: ['das-canary-scorecard-powerusers', 'das-canary-scorecard-viewers'],
        // DEV sees the link only in non-prod (dev/alpha); hidden in beta/prod to match the route guard.
        nonProdOnlyAccessibleTo: ['das-finsuite-application-dev']
      },
      { label: 'Frame', href: '/kingpin/goals', tooltip: 'Kingpin Goal Reporting Tool' },
      { label: 'Huddle+', href: '/ea-huddle' },
      {
        label: 'Line Up',
        href: '/line-up',
        tooltip: 'Headcount vs. Plan, Roster and Open Roles by Leader',
        // Mirrors HEADCOUNT_ACCESS_LDAP_GROUPS (eLDAPGroup.LINE_UP_LDAP), inlined like Canary above.
        accessibleTo: ['das-finsuite-line-up']
      },
      { label: 'MBR & Roadmap Docs', href: '/docs' },
      { label: 'MCP Servers', href: '/mcp' },
      { label: 'Operational Excellence', href: '/operational-excellence' },
      // Open to every signed-in user in every stage — no accessibleTo/hiddenInProd gate (same as Airspace above).
      { label: 'Signal', href: '/signal', tooltip: 'Asana project/portfolio prioritization' },
      { label: 'StarGrid', href: '/star-grid', tooltip: 'High-cardinality P&L / financial reporting (StarRocks)', nonProdOnly: true },
      { label: 'Wellness Tracker', href: '/org-tools/wellness-tracker', tooltip: 'DaS FinTech Summer Wellness Leaderboard' }
    ]
  }
];
